import React, { useContext, useEffect, useState, memo, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Page, Document, pdfjs } from 'react-pdf';
import { Buffer } from 'buffer';
import axios from 'axios';
import { PDFDocument } from 'pdf-lib';
import MergeButtons from './MergeButtons';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Pagination,
  TextField,
  Typography,
} from '@mui/material';
import { gql, useMutation, useLazyQuery } from '@apollo/client';
import uniqueId from 'lodash.uniqueid';

import { IMergeArray } from '../../types/merge';
import { MergeContext } from '../../context/MergeContext';
import { mergeItems, signItems, dateFormatOptions } from '../../utils/Constants';
import LoadingWrapper from '../common/LoadingWrapper';
import { useSelector } from 'react-redux';
import { selectProject } from '../../features/project/projectSlice';
import { selectUser } from '../../features/auth/authSlice';
import { selectCount, selectMerges, setMerges } from '../../features/merge/mergeSlice';
import { useAppDispatch } from '../../app/hooks';
import { useCreateActivity } from '../../features/activity/activityHooks';
import { showSuccessSnackbar, showErrorSnackbar } from '../../features/snackbar/snackbarSlice';
import { FlexEnd } from '../../commonStyles';
import { DraggableField } from '../common/dnd/Context';
import MergeInputs from './MergeInputs';
import MergeFieldList from './MergeFieldList';
import 'pdfjs-dist/build/pdf.worker.entry';
import { IUnitHistory } from '../../types/unit';
import { checkSpecialChars } from '../../utils/Functions';
import { GlobalModal } from '../../features/modal/Modal';
import { handleModal } from '../../features/modal/modalSlice';
import { getUnitMergeConfig } from './unitMergeConfig';
pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.js', import.meta.url).toString();

// Groups a flat, `category`-tagged token list (mergeItems/signItems in Constants.tsx) into
// { name, start, end } pages, one per run of consecutive same-category items. Adding a new
// token just needs a `category` in Constants.tsx -- start/end here are derived automatically.
const buildFieldPages = (items: { category: string }[]) => {
  const pages: { name: string; start: number; end: number }[] = [];
  items.forEach((item, index) => {
    const current = pages[pages.length - 1];
    if (current && current.name === item.category) {
      current.end = index + 1;
    } else {
      pages.push({ name: item.category, start: index, end: index + 1 });
    }
  });
  return pages;
};

const mergeFieldList = buildFieldPages(mergeItems);
const signFieldList = buildFieldPages(signItems);

const MergeCreator = memo(() => {
  const {
    pdfs,
    setRef,
    pageNum,
    setDocumentLoaded,
    selectedSuite,
    totalPages,
    setTotalPages,
    mergeName,
    mergeType,
    mergeFields,
    signFields,
    putUrl,
    suites,
    setSuites,
    setSelectedSuite,
  } = useContext(MergeContext);
  const storeDispatch = useAppDispatch();
  const createActivity = useCreateActivity();
  const navigate = useNavigate();
  const ref = useRef<any>(null);
  const widthRef = useRef<any>(null);
  const { mergeid } = useParams();
  const unitMerge = getUnitMergeConfig(mergeid);
  const project = useSelector(selectProject);
  const user = useSelector(selectUser);
  const merges = useSelector(selectMerges);
  const count = useSelector(selectCount);
  const [mergeFieldPage, setMergeFieldPage] = useState<number>(1);

  const [mergeCoordinates, setMergeCoordinates] = useState<IMergeArray[]>([]);
  const [signCoordinates, setSignCoordinates] = useState<IMergeArray[]>([]);
  const [file, setFile] = useState<any>(null);
  const [search, setSearch] = useState<any>('');
  // Shifting Merge Fields
  const [shiftNumber, setShiftNumber] = useState<string>('');
  const [shiftPage, setShiftPage] = useState<number>(0);
  const [savingAll, setSavingAll] = useState<boolean>(false);
  const [saveAllOpen, setSaveAllOpen] = useState<boolean>(false);
  // Floorplans: save the current fields to just the suites picked in the "Save for select" popup
  const [saveSelectOpen, setSaveSelectOpen] = useState<boolean>(false);
  const [saveSelectSuites, setSaveSelectSuites] = useState<any[]>([]);
  // Regular merge templates: "Save for select" copies the fields onto other merge templates in the project instead
  const [templateOptions, setTemplateOptions] = useState<any[]>([]);
  const [getTemplateOptions, { loading: templateOptionsLoading }] = useLazyQuery(MERGETEMPLATEOPTIONS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setTemplateOptions(
        (data.mergeTemplateMany || []).filter(
          // Not this template, and not the per-suite floorplan/warranty templates (those have their own pages)
          (template: any) => template._id !== mergeid && template.type !== 'Warranty' && template.type !== 'Floorplan'
        )
      );
    },
    onError: (err) => console.log(err, 'err'),
  });

  let context = ref.current?.getContext('2d');
  let rect = ref.current?.getBoundingClientRect();

  let floorplanMerge = project.mergeTemplates.find((merge: any) => merge.name === 'floorplan');
  let floorplanPage = floorplanMerge?.apsTemplates.find(
    (aps: any) => mergeName === aps.name || (aps.apsTemplate && aps.apsTemplate._id === mergeid)
  );
  // APS sign fields past the floorplan's APS page are saved as their 1-based APS page, others 0-based. The server
  // (savePdf) relies on this: it adds the floorplan's (and warranty sheet's) totalPages to fields past each insert.
  const floorplanPageNumber: number = floorplanPage?.pageNumber || 0;
  // 1-based APS page -> stored page
  const toStoredPage = (apsPage: number) => (floorplanPageNumber && apsPage > floorplanPageNumber ? apsPage : apsPage - 1);

  useEffect(() => {
    if (unitMerge && selectedSuite) {
      mergeFloorplansDocuments();
    } else if (pdfs) {
      mergePDFDocuments(pdfs.url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfs, selectedSuite, ref]);

  const [createMerge] = useMutation(CREATEMERGETEMPLATE, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar(`${data.mergeTemplateCreateAndUpload.name} has been created!`));
      storeDispatch(setMerges({ items: [...merges, data.mergeTemplateCreateAndUpload], count: count + 1 }));
      createActivity({
        project: project._id,
        user: user._id,
        deal: null,
        title: `Create Template`,
        content: `Template ${data.mergeTemplateCreateAndUpload.name} has been created`,
      });
      navigate(`/${project._id}/dashboard/project-settings`);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [updateProjectMerge] = useMutation(UPDATEPROJECTMERGE, {
    onCompleted: (data) => {},
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [editMerge] = useMutation(UPDATEMERGETEMPLATE, {
    onCompleted: (data) => {
      updateProjectMerge({ variables: { project: project._id, mergeTemplate: data.mergeTemplateUpdateById.record._id } });
      createActivity({
        project: project._id,
        user: user._id,
        deal: null,
        title: `Update Template`,
        content: `Template ${data.mergeTemplateUpdateById.record.name} has been updated`,
      });
      storeDispatch(showSuccessSnackbar('Merge Template has been saved!'));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  // Callback-free versions for Save for All, so each suite doesn't fire its own snackbar/activity
  const [createUnitTemplate] = useMutation(CREATEFLOORPLANTEMPLATE);
  const [updateUnitTemplate] = useMutation(UPDATEMERGETEMPLATE);
  const [linkUnitTemplate] = useMutation(UPDATEUNIT);

  const [updateUnit] = useMutation(UPDATEUNIT, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar(`${unitMerge?.label} Merge Templates has been updated!`));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [createFloorPlanTemplate] = useMutation(CREATEFLOORPLANTEMPLATE, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar(`${unitMerge?.label} Merge Templates has been updated!`));
      // Add mergeTemplateId to Unit
      updateUnit({
        variables: {
          _id: selectedSuite._id,
          record: {
            [unitMerge!.templateField]: data.mergeTemplateCreateOne.record._id,
            history: [
              ...selectedSuite.history.map((history: IUnitHistory) => {
                return {
                  ...history,
                  user: history.user ? history.user._id : null,
                };
              }),
              {
                type: 'Edit',
                description: `${unitMerge?.label} Merge Template has been created`,
                timestamp: new Date(),
                user: user._id,
              },
            ],
          },
        },
      });
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const mergeFloorplansDocuments = async () => {
    const url = selectedSuite[unitMerge!.urlField];
    const existingPdfBytes = await fetch(url).then((res) => res.arrayBuffer());

    const pdfDoc = await PDFDocument.load(existingPdfBytes);

    const pageCount = await pdfDoc.getPageCount();
    setTotalPages(pageCount);

    await pdfDoc.save();

    await setRef(ref);
    return;
  };

  async function mergePDFDocuments(documents: any) {
    const url = pdfs;
    const existingPdfBytes = await fetch(url).then((res) => res.arrayBuffer());
    const pdfDoc = await PDFDocument.load(existingPdfBytes, { ignoreEncryption: true });

    let pageCount = 0;
    try {
      pageCount = await pdfDoc.getPageCount();
    } catch (e) {
      return storeDispatch(showErrorSnackbar('There is an error with the PDF. PDF File may not be compliant'));
    }

    if (signCoordinates.length) {
      let newSignCoordinates = signCoordinates.filter((mergeCoordinate: IMergeArray) => mergeCoordinate.pageNumber! <= pageCount);
      setSignCoordinates(newSignCoordinates);
    }

    if (mergeCoordinates.length) {
      let newMergeCoordinates = mergeCoordinates.filter((mergeCoordinate: IMergeArray) => mergeCoordinate.pageNumber! < pageCount);
      setMergeCoordinates(newMergeCoordinates);
    }

    setTotalPages(pageCount);

    await pdfDoc.save();

    await setRef(ref);
    return;
  }

  const mergeFieldCoordinates = (
    mergeTitle: string,
    mergeSize: number,
    x: number,
    docuSignY: number,
    mergeIndex: number,
    mergeFormat: string,
    wrap: number
  ) => {
    setMergeCoordinates([
      ...mergeCoordinates,
      {
        mergeId: uniqueId(),
        pageNumber: pageNum - 1,
        key: mergeTitle,
        format: mergeFormat,
        fontSize: mergeSize,
        index: mergeIndex,
        x: x,
        y: docuSignY,
        wrap: wrap,
      },
    ]);
  };

  const signFieldCoordinates = (mergeTitle: string, x: number, docuSignY: number, mergeIndex: number, mergeName: string) => {
    let docuSignCoord = docuSignY;
    setSignCoordinates([
      ...signCoordinates,
      {
        mergeId: uniqueId(),
        pageNumber: toStoredPage(pageNum),
        key: mergeTitle,
        title: mergeName,
        index: mergeIndex,
        x: x,
        y: docuSignCoord,
      },
    ]);
  };

  const getCursorPosition = (
    canvas: any,
    event: any,
    mergeTitle: string,
    mergeType: string,
    mergeIndex: number,
    mergeSize: number = 12,
    format: string,
    moveElement: boolean,
    wrap: number
  ) => {
    if (!canvas) {
      return;
    }
    let rect = canvas.current?.getBoundingClientRect();
    let x = event.activatorEvent.x - rect.left;
    let y = event.activatorEvent.y - rect.top - 1;

    if (!moveElement) {
      if (
        event.delta.x + event.activatorEvent.x > rect.left &&
        event.delta.x + event.activatorEvent.x < rect.right &&
        event.delta.y + event.activatorEvent.y < rect.bottom &&
        event.delta.y + event.activatorEvent.y > rect.top
      ) {
        x = event.delta.x - rect.left + event.activatorEvent.x;

        if (rect.top !== 64) {
          if (rect.top < 0) {
            if (event.delta.y < 0) {
              y = event.activatorEvent.y + Math.abs(rect.top) - Math.abs(event.delta.y);
            } else {
              y = event.activatorEvent.y + Math.abs(rect.top) + Math.abs(event.delta.y);
            }
          } else if (rect.top > 0) {
            if (event.delta.y < 0) {
              y = event.activatorEvent.y - Math.abs(rect.top) - Math.abs(event.delta.y);
            } else {
              y = event.activatorEvent.y - Math.abs(rect.top) + Math.abs(event.delta.y);
            }
          }
        } else if (event.delta.y < 0) {
          y = event.activatorEvent.y - rect.top - Math.abs(event.delta.y);
        } else y = event.activatorEvent.y - rect.top + Math.abs(event.delta.y);
      } else {
        // removed merge field from pdf
        let filteredMergeCoordinates = mergeCoordinates.filter((merge: any) => merge.mergeId !== event.active.data.current.merge.mergeId);
        setMergeCoordinates(filteredMergeCoordinates);

        let filteredSignCoordinates = signCoordinates.filter((merge: any) => merge.mergeId !== event.active.data.current.merge.mergeId);
        setSignCoordinates(filteredSignCoordinates);
      }

      if (x > context.canvas.clientWidth || x < 0) {
        return;
      }
      if (y > context.canvas.clientHeight || y < 0) {
        return;
      }

      if (mergeType === 'merge') {
        mergeFieldCoordinates(`[${mergeTitle}]`, mergeSize, x, y, mergeIndex, format, wrap);
      } else {
        signFieldCoordinates(mergeType, x, y, mergeIndex, mergeTitle);
      }
    } else {
      // Moving an element again or element created from database
      let x = event.active.data.current.merge.x;
      let y = event.active.data.current.merge.y;

      if (
        event.delta.x + event.activatorEvent.x > rect.left &&
        event.delta.x + event.activatorEvent.x < rect.right &&
        event.delta.y + event.activatorEvent.y < rect.bottom &&
        event.delta.y + event.activatorEvent.y > rect.top
      ) {
        if (event.delta.x > 0) {
          x = event.active.data.current.merge.x + event.delta.x;
        } else if (event.delta.x < 0) {
          x = event.active.data.current.merge.x + event.delta.x;
        }

        if (event.delta.y > 0) {
          y = event.active.data.current.merge.y + event.delta.y;
        } else if (event.delta.y < 0) {
          y = event.active.data.current.merge.y + event.delta.y;
        }

        if (mergeType === 'merge') {
          let newMergeCoordinates = mergeCoordinates.map((mergeCoord: any) => {
            if (mergeCoord.mergeId === event.active.id) {
              return {
                ...mergeCoord,
                x,
                y,
              };
            } else return mergeCoord;
          });
          setMergeCoordinates(newMergeCoordinates);
        } else {
          let newSignCoordinates = signCoordinates.map((mergeCoord: any) => {
            if (mergeCoord.mergeId === event.active.id) {
              return {
                ...mergeCoord,
                x,
                y,
              };
            } else return mergeCoord;
          });
          setSignCoordinates(newSignCoordinates);
        }
      } else {
        let filteredMergeCoordinates = mergeCoordinates.filter((merge: any) => merge.mergeId !== event.active.data.current.merge.mergeId);
        setMergeCoordinates(filteredMergeCoordinates);

        let filteredSignCoordinates = signCoordinates.filter((merge: any) => merge.mergeId !== event.active.data.current.merge.mergeId);
        setSignCoordinates(filteredSignCoordinates);
      }
    }
  };

  // Strips the editor-only props (mergeId, displayWrap, bracketed keys) off the placed fields
  const buildSaveFields = () => {
    // signCoordinates already carries the floorplan-adjusted pageNumber from signFieldCoordinates
    // (via toStoredPage) — no further adjustment is needed here; the server shifts for the floorplan/warranty pages.
    let sign = signCoordinates.map((sign: any) => {
      return {
        index: sign.index,
        key: sign.key,
        pageNumber: sign.pageNumber,
        title: sign.title ? sign.title : '',
        x: sign.x,
        y: sign.y,
      };
    });

    let newMergeCoordinates = mergeCoordinates.map((mergeCoords: any) => {
      return {
        format: mergeCoords.format,
        key: mergeCoords.key.replace(/[[\]']+/g, '').replace(/\s/g, ''),
        index: mergeCoords.index,
        pageNumber: mergeCoords.pageNumber,
        x: mergeCoords.x,
        y: mergeCoords.y,
        fontSize: mergeCoords.fontSize,
        wrap: parseInt(mergeCoords.wrap, 10),
      };
    });

    return { mergeFields: newMergeCoordinates, signFields: sign };
  };

  const saveMerge = async () => {
    if (!unitMerge && !mergeName && !mergeType) {
      storeDispatch(showErrorSnackbar(`Title or Template Type is missing`));
      return;
    }

    const { mergeFields: newMergeCoordinates, signFields: sign } = buildSaveFields();

    let selectedProject = project._id;

    if (mergeType === 'Listing' || mergeType === 'Tenancy' || mergeType === 'Management') {
      selectedProject = '65bd404201d843802bb37a51';
    }

    let mergeObject = {
      project: selectedProject,
      name: unitMerge ? selectedSuite.suite : mergeName,
      mergeFields: newMergeCoordinates,
      signFields: sign,
      type: unitMerge ? unitMerge.type : mergeType,
      totalPages: totalPages,
    };

    if (checkSpecialChars(mergeName)) {
      return storeDispatch(showErrorSnackbar('Name contains special characters'));
    }

    if (mergeid) {
      if (unitMerge) {
        const unitTemplate = selectedSuite[unitMerge.templateField];
        if (!unitTemplate) {
          createFloorPlanTemplate({ variables: { record: mergeObject } });
        } else {
          await editMerge({ variables: { _id: unitTemplate._id, record: mergeObject } });
        }
      } else {
        if (file) {
          let newMergeFields = mergeObject.mergeFields.filter((merge: any) => merge.pageNumber <= mergeObject.totalPages);
          let newSignFields = mergeObject.signFields.filter((merge: any) => merge.pageNumber <= mergeObject.totalPages);

          let buffer: any;
          if (pdfs.indexOf('application/pdf') !== -1) {
            buffer = Buffer.from(pdfs.replace(/^data:application\/\w+;base64,/, ''), 'base64');
          } else {
            buffer = Buffer.from(pdfs.replace(/^data:image\/\w+;base64,/, ''), 'base64');
          }

          const options = {
            headers: { 'Content-Type': `${file.type}` },
            maxContentLength: Infinity,
            maxBodyLength: Infinity,
          };

          mergeObject = {
            ...mergeObject,
            mergeFields: newMergeFields,
            signFields: newSignFields,
          };

          if (mergeType === 'Management' || mergeType === 'Listing' || mergeType === 'Tenancy') {
            mergeObject.project = '65bd404201d843802bb37a51';
          }

          await axios.put(putUrl, buffer, options);
        }

        await editMerge({ variables: { _id: mergeid, record: mergeObject } });
      }
    } else {
      createMerge({ variables: { record: mergeObject, file: file, recordId: true } });
    }
  };

  // Only suites that actually have a PDF for this page (e.g. an uploaded warranty) get saved
  const pdfSuites = unitMerge
    ? suites.filter((suite: any) => suite[unitMerge.urlField])
    : [];

  // Applies the current merge/sign fields to the given suites' templates on this page
  // (every suite with a PDF for "Save for all", or the picked ones for "Save for select")
  const saveForSuites = async (targets: any[], scope: 'all' | 'selected') => {
    if (!unitMerge || !targets.length) return;

    const { mergeFields: newMergeFields, signFields: newSignFields } = buildSaveFields();
    const templateField = unitMerge.templateField;
    setSavingAll(true);

    const saveSuite = async (suite: any) => {
      const mergeObject = {
        project: project._id,
        name: suite.suite,
        mergeFields: newMergeFields,
        signFields: newSignFields,
        type: unitMerge.type,
        totalPages: totalPages,
      };
      const template = suite[templateField];

      if (template) {
        await updateUnitTemplate({ variables: { _id: template._id, record: mergeObject } });
        await updateProjectMerge({ variables: { project: project._id, mergeTemplate: template._id } });
        return { ...suite, [templateField]: { ...template, mergeFields: newMergeFields, signFields: newSignFields } };
      }

      const res = await createUnitTemplate({ variables: { record: mergeObject } });
      const templateId = res.data.mergeTemplateCreateOne.record._id;
      await linkUnitTemplate({
        variables: {
          _id: suite._id,
          record: {
            [templateField]: templateId,
            history: [
              ...suite.history.map((history: IUnitHistory) => ({ ...history, user: history.user ? history.user._id : null })),
              {
                type: 'Edit',
                description: `${unitMerge.label} Merge Template has been created`,
                timestamp: new Date(),
                user: user._id,
              },
            ],
          },
        },
      });
      return { ...suite, [templateField]: { _id: templateId, mergeFields: newMergeFields, signFields: newSignFields } };
    };

    // Save in small batches so large projects don't fire hundreds of requests at once
    const updatedSuites: any[] = [];
    let failed = 0;
    for (let i = 0; i < targets.length; i += 10) {
      const results = await Promise.allSettled(targets.slice(i, i + 10).map(saveSuite));
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          updatedSuites.push(result.value);
        } else {
          failed++;
          updatedSuites.push(targets[i + index]);
        }
      });
    }

    // Keep local suites in sync so the next save updates these templates instead of creating duplicates
    setSuites(suites.map((suite: any) => updatedSuites.find((updated: any) => updated._id === suite._id) || suite));
    setSelectedSuite(updatedSuites.find((suite: any) => suite._id === selectedSuite._id) || selectedSuite);
    setSavingAll(false);

    createActivity({
      project: project._id,
      user: user._id,
      deal: null,
      title: `Update Template`,
      content:
        scope === 'all'
          ? `${unitMerge.label} Merge Templates have been saved for all suites`
          : `${unitMerge.label} Merge Templates have been saved for suites ${targets.map((suite: any) => suite.suite).join(', ')}`,
    });

    if (failed) {
      storeDispatch(showErrorSnackbar(`Saved ${targets.length - failed} of ${targets.length} suites. ${failed} failed.`));
    } else {
      storeDispatch(
        showSuccessSnackbar(
          scope === 'all'
            ? `${unitMerge.label} Merge Templates saved for all ${targets.length} suites!`
            : `${unitMerge.label} Merge Templates saved for ${targets.length} selected ${targets.length === 1 ? 'suite' : 'suites'}!`
        )
      );
    }
  };

  const saveForAll = async () => {
    setSaveAllOpen(false);
    await saveForSuites(pdfSuites, 'all');
  };

  // Copies the current merge/sign fields onto other merge templates (their name, type and PDF are left alone)
  const saveToTemplates = async (targets: any[]) => {
    if (!targets.length) return;
    const { mergeFields: newMergeFields, signFields: newSignFields } = buildSaveFields();
    setSavingAll(true);

    let failed = 0;
    for (let i = 0; i < targets.length; i += 10) {
      const results = await Promise.allSettled(
        targets
          .slice(i, i + 10)
          .map((template: any) => updateUnitTemplate({ variables: { _id: template._id, record: { mergeFields: newMergeFields, signFields: newSignFields } } }))
      );
      failed += results.filter((result) => result.status === 'rejected').length;
    }
    setSavingAll(false);

    createActivity({
      project: project._id,
      user: user._id,
      deal: null,
      title: `Update Template`,
      content: `Merge and sign fields from ${mergeName || 'this template'} were saved to ${targets.map((template: any) => template.name).join(', ')}`,
    });

    if (failed) {
      storeDispatch(showErrorSnackbar(`Saved ${targets.length - failed} of ${targets.length} templates. ${failed} failed.`));
    } else {
      storeDispatch(showSuccessSnackbar(`Saved to ${targets.length} ${targets.length === 1 ? 'template' : 'templates'}!`));
    }
  };

  const openSaveForSelect = () => {
    if (!unitMerge) getTemplateOptions({ variables: { filter: { project: project._id } } });
    setSaveSelectOpen(true);
  };

  const saveForSelect = async () => {
    setSaveSelectOpen(false);
    const targets = saveSelectSuites;
    setSaveSelectSuites([]);
    // The current merge is always saved too, along with the ones picked
    if (unitMerge) {
      const withCurrent = targets.some((suite: any) => suite._id === selectedSuite._id) ? targets : [selectedSuite, ...targets];
      await saveForSuites(withCurrent, 'selected');
    } else {
      await saveMerge();
      await saveToTemplates(targets);
    }
  };

  function onDocumentLoadSuccess({ numPages: nextNumPages }: { numPages: any }) {
    // Only set coordinates on initial load
    if (unitMerge) {
      setMergeCoordinates(
        mergeFields.map((merge: any) => {
          return { ...merge, mergeId: uniqueId() };
        })
      );
      setSignCoordinates(
        signFields.map((merge: any) => {
          return { ...merge, mergeId: uniqueId() };
        })
      );
    } else {
      if (mergeFields.length && !mergeCoordinates.length) {
        setMergeCoordinates(
          mergeFields.map((merge: any) => {
            let revisedKey = `[${merge.key}]`;
            let wrapNumber = ' '.repeat(parseInt(merge.wrap, 10));
            let trimmedKey = revisedKey.replace(/\s/g, '');
            let string = trimmedKey.substring(0, trimmedKey.length - 1) + wrapNumber + trimmedKey.substring(trimmedKey.length - 1);
            return { ...merge, key: string, mergeId: uniqueId() };
          })
        );
      }

      if (signFields.length && !signCoordinates.length) {
        setSignCoordinates(
          signFields.map((merge: any) => {
            return { ...merge, mergeId: uniqueId() };
          })
        );
      }
    }

    setDocumentLoaded(true);
  }

  const handleDragEnd = (event: any, mergeItem: any) => {
    getCursorPosition(
      ref,
      event,
      event.active.data.current.merge.name ? event.active.data.current.merge.name : event.active.data.current.merge.key,
      event.active.data.current.merge.hasOwnProperty('format') ? 'merge' : 'sign',
      event.active.data.current.merge.index,
      event.active.data.current.merge.fontSize,
      event.active.data.current.merge.format ? event.active.data.current.merge.format : '',
      true,
      mergeItem.wrap
    );
  };

  // Sign fields are stored at their page in the generated document, so compare against the current page's stored page
  const getElementDisplay = (pageNumber: number) => (pageNumber === toStoredPage(pageNum) ? 'inline-block' : 'none');

  const handleMergeCoordinates = (value: string, numIndex: number) => {
    let newMergeCoordinates = mergeCoordinates.map((mergeCoords: any, index: number) => {
      if (index === numIndex) {
        return {
          ...mergeCoords,
          wrap: value,
        };
      } else return mergeCoords;
    });
    setMergeCoordinates(newMergeCoordinates);
  };

  const handleBlurWrap = (e: any, numIndex: number) => {
    let newMergeCoordinates = mergeCoordinates.map((mergeCoords: any, index: number) => {
      if (index === numIndex) {
        let wrapNumber = ' '.repeat(parseInt(mergeCoords.wrap, 10));
        let trimmedKey = mergeCoords.key.replace(/\s/g, '');
        let string = trimmedKey.substring(0, trimmedKey.length - 1) + wrapNumber + trimmedKey.substring(trimmedKey.length - 1);
        return {
          ...mergeCoords,
          key: string,
          displayWrap: !mergeCoords.displayWrap,
        };
      } else return mergeCoords;
    });
    setMergeCoordinates(newMergeCoordinates);
  };

  const handleFormatChange = (value: string, numIndex: number) => {
    let newMergeCoordinates = mergeCoordinates.map((mergeCoords: any, index: number) => {
      if (index === numIndex) {
        return {
          ...mergeCoords,
          format: value,
        };
      } else return mergeCoords;
    });
    setMergeCoordinates(newMergeCoordinates);
  };

  const showWrap = (mergeField: any) => {
    let newMergeCoordinates = mergeCoordinates.map((mergeCoords: any, index: number) => {
      if (mergeCoords.mergeId === mergeField.mergeId) {
        return {
          ...mergeCoords,
          displayWrap: !mergeCoords.displayWrap,
        };
      } else return mergeCoords;
    });
    setMergeCoordinates(newMergeCoordinates);
  };

  // Copies the previous APS page's sign fields onto the current page, accounting for any insert between them
  const copySignFields = () => {
    if (pageNum <= 1) return;
    const previousStoredPage = toStoredPage(pageNum - 1);
    const currentStoredPage = toStoredPage(pageNum);
    const duplicateSignFields = signCoordinates
      .filter((signCoordinates: IMergeArray) => signCoordinates.pageNumber === previousStoredPage)
      .map((signCoordinates: IMergeArray) => {
        return {
          ...signCoordinates,
          pageNumber: currentStoredPage,
          mergeId: uniqueId(),
        };
      });
    setSignCoordinates([...signCoordinates, ...duplicateSignFields]);
  };

  const handleShift = () => {
    if (!shiftNumber || !shiftPage) {
      return storeDispatch(showErrorSnackbar('Missing Fields'));
    }

    let newSignFields = [];
    let newMergeFields = [];

    for (const signField of signFields) {
      if (signField.pageNumber > shiftPage) {
        newSignFields.push({
          ...signField,
          pageNumber: signField.pageNumber! + parseInt(shiftNumber, 10),
        });
      } else if (signField.pageNumber === shiftPage) {
        if (parseInt(shiftNumber, 10) > 0) {
          newSignFields.push({
            ...signField,
            pageNumber: signField.pageNumber! + parseInt(shiftNumber, 10),
          });
        } else {
          continue;
        }
      } else {
        newSignFields.push(signField);
      }
    }

    for (const mergeField of mergeFields) {
      if (mergeField.pageNumber > shiftPage) {
        newMergeFields.push({
          ...mergeField,
          pageNumber: mergeField.pageNumber! + parseInt(shiftNumber, 10),
        });
      } else if (mergeField.pageNumber === shiftPage) {
        if (parseInt(shiftNumber, 10) > 0) {
          newMergeFields.push({
            ...mergeField,
            pageNumber: mergeField.pageNumber! + parseInt(shiftNumber, 10),
          });
        } else if (parseInt(shiftNumber, 10) < 0) {
          newMergeFields.push({
            ...mergeField,
            pageNumber: mergeField.pageNumber! + parseInt(shiftNumber, 10),
          });
        } else {
          continue;
        }
      } else {
        if (parseInt(shiftNumber, 10) > 0 && mergeField.pageNumber + 1 === shiftPage) {
          newMergeFields.push({
            ...mergeField,
            pageNumber: mergeField.pageNumber! + parseInt(shiftNumber, 10),
          });
        } else {
          newMergeFields.push(mergeField);
        }
      }
    }

    setSignCoordinates(newSignFields);
    setMergeCoordinates(newMergeFields);
    storeDispatch(handleModal(false));
    storeDispatch(showSuccessSnackbar('Merge and Sign Fields have been shifted!'));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignSelf: 'flex-start',
        backgroundColor: '#f5f5f5',
      }}
    >
      <GlobalModal>
        <Box>
          <Typography variant="h4">
            <strong>Shift Merge Fields</strong>
          </Typography>
          <Box sx={{ my: 2 }}>
            If you are shifting all merge fields down a page, please enter a negative number under Number of Shifts. If you are shifting all
            merge fields up a page, please enter a positive number under Number of Shifts..
          </Box>
          <TextField
            sx={{ mt: 2 }}
            title={'Number of Shifts By Page Number (ie. -1, 1, 2, etc)'}
            name={'shifts'}
            fullWidth
            value={shiftNumber}
            label={'Number of Shifts'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShiftNumber(e.target.value)}
          />
          <TextField
            sx={{ mt: 2 }}
            title={'Which page to shift merge fields on?'}
            name={'shiftPage'}
            type="number"
            onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
            fullWidth
            value={shiftPage}
            label={'Which page to shift merge fields on?'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShiftPage(parseInt(e.target.value, 10))}
          />
          <FlexEnd sx={{ mt: 2 }}>
            <Button
              color="error"
              variant="contained"
              sx={{ mr: 1 }}
              onClick={() => {
                setShiftNumber('');
                setShiftPage(0);
                storeDispatch(handleModal(false));
              }}
            >
              Do not Shift
            </Button>
            <Button
              color="success"
              variant="contained"
              onClick={() => {
                handleShift();
              }}
            >
              Confirm
            </Button>
          </FlexEnd>
        </Box>
      </GlobalModal>
      <Paper
        elevation={12}
        sx={{
          width: '30%',
          p: 2,
          position: 'sticky',
          top: 0,
          bottom: 0,
          left: 0,
          height: '100%',
          zIndex: 20,
        }}
      >
        <MergeInputs setFile={setFile} />
        <Box sx={{ mt: 1 }}>
          <TextField
            title={'Search Merge'}
            name={'search'}
            fullWidth
            value={search}
            label={'Search Merge'}
            size="small"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setSearch(e.target.value);
            }}
          />
        </Box>
        <Box sx={{ height: '570px', zIndex: 20 }}>
          {search ? (
            <Box>
              <MergeFieldList
                fieldName={'Search Fields'}
                start={0}
                end={10000}
                getCursorPosition={getCursorPosition}
                mergeItems={[...mergeItems, ...signItems]
                  .filter((merge: any) => merge.name.toLowerCase().includes(search.toLowerCase()))
                  .slice(0, 45)}
              />
            </Box>
          ) : (
            <Box>
              {mergeFieldList.length + 1 === mergeFieldPage ? (
                <MergeFieldList
                  fieldName={signFieldList[0].name}
                  start={signFieldList[0].start}
                  end={signFieldList[0].end}
                  getCursorPosition={getCursorPosition}
                  mergeItems={signItems}
                />
              ) : null}
              {mergeFieldList.length + 2 === mergeFieldPage ? (
                <MergeFieldList
                  fieldName={signFieldList[1].name}
                  start={signFieldList[1].start}
                  end={signFieldList[1].end}
                  getCursorPosition={getCursorPosition}
                  mergeItems={signItems}
                />
              ) : null}
              {mergeFieldList.length + 3 === mergeFieldPage ? (
                <MergeFieldList
                  fieldName={signFieldList[2].name}
                  start={signFieldList[2].start}
                  end={signFieldList[2].end}
                  getCursorPosition={getCursorPosition}
                  mergeItems={signItems}
                />
              ) : null}
              {mergeFieldPage <= mergeFieldList.length ? (
                <MergeFieldList
                  fieldName={mergeFieldList[mergeFieldPage - 1].name}
                  start={mergeFieldList[mergeFieldPage - 1].start}
                  end={mergeFieldList[mergeFieldPage - 1].end}
                  getCursorPosition={getCursorPosition}
                  mergeItems={mergeItems}
                />
              ) : null}
              <Box sx={{ mt: 2 }}>
                <Pagination
                  sx={{ '& .MuiPagination-ul': { justifyContent: 'center' } }}
                  color="secondary"
                  onChange={(e, page) => setMergeFieldPage(page)}
                  count={mergeFieldList.length + 3}
                />
              </Box>
            </Box>
          )}
        </Box>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Link style={{ textDecoration: 'none' }} to={`/${project._id}/dashboard/create-merges`}>
            <Button sx={{ mr: 1 }} variant="contained" color="primary" size="small" startIcon={<ArrowBackIcon />}>
              Back
            </Button>
          </Link>
          <Button variant="contained" color="success" size="small" startIcon={<CloudUploadIcon />} onClick={saveMerge} disabled={savingAll}>
            Save
          </Button>
          {unitMerge?.templateField === 'warrantySheet' ? (
            <Button
              sx={{ ml: 1 }}
              variant="contained"
              color="success"
              size="small"
              startIcon={<CloudUploadIcon />}
              onClick={() => setSaveAllOpen(true)}
              disabled={savingAll || !pdfSuites.length}
            >
              {savingAll ? 'Saving...' : 'Save for all'}
            </Button>
          ) : null}
          {unitMerge?.templateField === 'floorPlan' || (!unitMerge && mergeid) ? (
            <Button
              sx={{ ml: 1 }}
              variant="contained"
              color="success"
              size="small"
              startIcon={<CloudUploadIcon />}
              onClick={openSaveForSelect}
              disabled={savingAll || (unitMerge ? !pdfSuites.length : false)}
            >
              {savingAll ? 'Saving...' : 'Save for select'}
            </Button>
          ) : null}
          <Dialog open={saveAllOpen} onClose={() => setSaveAllOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>Save for all suites</DialogTitle>
            <DialogContent>
              <Typography>
                This will replace the {unitMerge?.label.toLowerCase()} merge and sign fields for all <strong>{pdfSuites.length}</strong>{' '}
                {pdfSuites.length === 1 ? 'suite' : 'suites'} with a {unitMerge?.label.toLowerCase()} PDF, using the fields placed on the
                current suite.
              </Typography>
              <Typography sx={{ mt: 1.5 }}>Any fields previously saved on those suites will be overwritten.</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSaveAllOpen(false)}>Cancel</Button>
              <Button onClick={saveForAll} variant="contained" color="success">
                Save for all {pdfSuites.length}
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={saveSelectOpen}
            onClose={() => {
              setSaveSelectOpen(false);
              setSaveSelectSuites([]);
            }}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>{unitMerge ? 'Save for selected suites' : 'Save for selected templates'}</DialogTitle>
            <DialogContent>
              <Typography>
                {unitMerge
                  ? `Choose the suites to save the ${unitMerge.label.toLowerCase()} merge and sign fields to, using the fields placed on the current suite. The current suite is saved too. Only suites with a ${unitMerge.label.toLowerCase()} PDF are listed.`
                  : 'Choose the merge templates to copy these merge and sign fields to. This template is saved too; the selected ones keep their names, types and PDFs.'}
              </Typography>
              <Autocomplete
                sx={{ mt: 2 }}
                multiple
                disableCloseOnSelect
                loading={!unitMerge && templateOptionsLoading}
                options={unitMerge ? pdfSuites : templateOptions}
                getOptionLabel={(option: any) => (unitMerge ? `${option.suite}` : `${option.name}${option.type ? ` (${option.type})` : ''}`)}
                isOptionEqualToValue={(option: any, value: any) => option._id === value._id}
                value={saveSelectSuites}
                onChange={(_, value) => setSaveSelectSuites(value)}
                renderInput={(params) => (
                  <TextField {...params} label={unitMerge ? 'Suites' : 'Merge Templates'} placeholder={unitMerge ? 'Select suites' : 'Select templates'} />
                )}
              />
              <Typography sx={{ mt: 1.5 }}>
                Any fields previously saved on the selected {unitMerge ? 'suites' : 'templates'} will be overwritten.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setSaveSelectOpen(false);
                  setSaveSelectSuites([]);
                }}
              >
                Cancel
              </Button>
              <Button onClick={saveForSelect} variant="contained" color="success" disabled={!saveSelectSuites.length}>
                Save for {saveSelectSuites.length}{' '}
                {unitMerge ? (saveSelectSuites.length === 1 ? 'suite' : 'suites') : saveSelectSuites.length === 1 ? 'template' : 'templates'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Paper>
      <Box
        ref={widthRef}
        sx={{
          zIndex: 1,
          width: '70%',
          height: '100%',
          position: 'relative',
          '& .react-pdf__Page__canvas': {
            margin: '0 auto',
          },
          '& .react-pdf__Page, .react-pdf__Document': {
            height: '100%',
          },
          '& .react-pdf__message--loading': {
            display: 'flex',
            justifyContent: 'center',
            alignSelf: 'center',
          },
          '& .react-pdf__message': {
            marginTop: '20px',
            textAlign: 'center',
          },
        }}
      >
        <Document
          style={{ position: 'relative' }}
          error={'An error has occurred or this pdf does not have that many pages.'}
          loading={<LoadingWrapper title="PDF is loading..." modal={false} />}
          file={pdfs}
          key={1}
        >
          {widthRef.current && rect ? (
            <>
              {mergeCoordinates.map((element: any, index: number) => {
                return (
                  <Box
                    key={index}
                    sx={{
                      position: 'absolute',
                      zIndex: '10000',
                      top: element.y - 7,
                      left:
                        rect.width >= 1224 && widthRef?.current?.offsetWidth >= 1224 && unitMerge
                          ? (widthRef?.current?.offsetWidth - rect.width) / 2 + element.x
                          : rect.width >= 1224 && unitMerge
                            ? element.x
                            : (widthRef?.current?.offsetWidth - rect.width) / 2 + element.x,
                      fontSize: element.fontSize,
                      display: element.pageNumber !== pageNum - 1 ? 'none' : 'inline-block',
                    }}
                  >
                    {element.displayWrap ? (
                      <Box
                        sx={{
                          position: 'absolute',
                          // Two inputs (wrap + decimal places / date format) need the popup raised higher
                          top: /price/i.test(element.key) || element.format ? '-90px' : '-40px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                        }}
                      >
                        <TextField
                          sx={{
                            width: '160px',
                            '& .MuiOutlinedInput-input': { p: 1 },
                            '& .MuiInputLabel-root': { color: '#000' },
                            backgroundColor: '#f5f5f5',
                          }}
                          type="number"
                          title={'Wrap Length'}
                          name={'wrap'}
                          onChange={(e) => handleMergeCoordinates(e.target.value, index)}
                          fullWidth
                          value={element.wrap}
                          label={'Wrap Length'}
                          onBlur={() => handleBlurWrap(element, index)}
                        />
                        {/* Price fields: how many decimal places to show; other fields with a format: the date format */}
                        {/price/i.test(element.key) ? (
                          <TextField
                            sx={{
                              width: '160px',
                              '& .MuiOutlinedInput-input': { p: 1 },
                              '& .MuiInputLabel-root': { color: '#000' },
                              backgroundColor: '#f5f5f5',
                            }}
                            type="number"
                            title={'Decimal Places'}
                            name={'format'}
                            inputProps={{ min: 0, max: 4, step: 1 }}
                            onChange={(e) => {
                              const decimals = Math.min(Math.max(parseInt(e.target.value, 10) || 0, 0), 4);
                              handleFormatChange(`${decimals}`, index);
                            }}
                            fullWidth
                            value={element.format ?? '0'}
                            label={'Decimal Places'}
                          />
                        ) : element.format ? (
                          <TextField
                            select
                            sx={{
                              width: '160px',
                              '& .MuiOutlinedInput-input': { p: 1 },
                              '& .MuiInputLabel-root': { color: '#000' },
                              backgroundColor: '#f5f5f5',
                            }}
                            title={'Date Format'}
                            name={'format'}
                            onChange={(e) => handleFormatChange(e.target.value, index)}
                            fullWidth
                            value={element.format}
                            label={'Date Format'}
                          >
                            {dateFormatOptions.map((dateFormat) => (
                              <MenuItem key={dateFormat.value} value={dateFormat.value}>
                                {dateFormat.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : null}
                      </Box>
                    ) : null}
                    <DraggableField reset={false} mergeItem={element} handleDragEnd={handleDragEnd} showWrap={showWrap} />
                  </Box>
                );
              })}
              {signCoordinates.map((element: any, index: number) => {
                return (
                  <Box
                    key={index}
                    sx={{
                      position: 'absolute',
                      zIndex: '10000',
                      top: element.y - 7,
                      left:
                        rect.width >= 1224 && widthRef?.current?.offsetWidth >= 1224 && unitMerge
                          ? (widthRef?.current?.offsetWidth - rect.width) / 2 + element.x
                          : rect.width >= 1224 && unitMerge
                            ? element.x
                            : (widthRef?.current?.offsetWidth - rect.width) / 2 + element.x,
                      fontSize: element.fontSize,
                      display: getElementDisplay(element.pageNumber),
                    }}
                  >
                    <DraggableField reset={false} mergeItem={element} handleDragEnd={handleDragEnd} showWrap={showWrap} />
                  </Box>
                );
              })}
            </>
          ) : null}
          <Page renderTextLayer={false} canvasRef={ref} scale={1} loading={''} pageNumber={pageNum} onLoadSuccess={onDocumentLoadSuccess} />
        </Document>
        {totalPages > 1 ? <MergeButtons totalPages={totalPages} copySignFields={copySignFields} /> : null}
      </Box>
    </Box>
  );
});

const MERGETEMPLATEOPTIONS = gql`
  query mergeTemplateMany($filter: FilterFindManyMergeTemplateInput) {
    mergeTemplateMany(filter: $filter, limit: 10000) {
      _id
      name
      type
    }
  }
`;

const UPDATEMERGETEMPLATE = gql`
  mutation mergeTemplateUpdateById($_id: MongoID!, $record: UpdateByIdMergeTemplateInput!) {
    mergeTemplateUpdateById(_id: $_id, record: $record) {
      record {
        _id
        project {
          _id
        }
        name
        mergeFields {
          key
          index
          pageNumber
          x
          y
          fontSize
          format
          wrap
        }
        signFields {
          key
          index
          title
          pageNumber
          x
          y
        }
        default
        type
        getUrl
        putUrl
      }
    }
  }
`;

const CREATEFLOORPLANTEMPLATE = gql`
  mutation mergeTemplateCreateOne($record: CreateOneMergeTemplateInput!) {
    mergeTemplateCreateOne(record: $record) {
      record {
        _id
        project {
          _id
        }
        mergeFields {
          key
          index
          pageNumber
          x
          y
          fontSize
          format
          wrap
        }
        signFields {
          key
          index
          pageNumber
          x
          y
          title
        }
        type
        name
      }
    }
  }
`;

const CREATEMERGETEMPLATE = gql`
  mutation mergeTemplateCreateAndUpload($record: CreateOneMergeTemplateInput!, $file: Upload!) {
    mergeTemplateCreateAndUpload(record: $record, file: $file) {
      _id
      project {
        _id
      }
      mergeFields {
        key
        index
        pageNumber
        x
        y
        fontSize
        format
        wrap
      }
      signFields {
        key
        index
        pageNumber
        x
        y
        title
      }
      type
      name
    }
  }
`;

const UPDATEUNIT = gql`
  mutation unitUpdateById($_id: MongoID!, $record: UpdateByIdUnitInput!) {
    unitUpdateById(_id: $_id, record: $record) {
      record {
        _id
        floorPlan {
          _id
        }
      }
    }
  }
`;

const UPDATEPROJECTMERGE = gql`
  mutation updateProjectMerge($project: MongoID!, $mergeTemplate: MongoID!) {
    updateProjectMerge(project: $project, mergeTemplate: $mergeTemplate) {
      _id
    }
  }
`;

export default MergeCreator;
